import { LeftOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Form } from 'antd'
import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  useCounterpartServiceCreateCounterpartEndpoint,
  useCounterpartServiceGetCounterpartEndpoint,
  useCounterpartServiceGetCounterpartEndpointKey,
  useCounterpartServiceGetCounterpartListEndpointKey,
  useCounterpartServiceUpdateCounterpartEndpoint
} from 'src/api/queries'
import { ApiError, LegalEntityStatus } from 'src/api/requests'
import {
  FormFooter,
  LoadingScreen,
  message,
  PageBreadcrumbs
} from 'src/components'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler } from 'src/utils'

import { CounterPartDetails } from '../components'

export const CounterPartsAdd = () => {
  const { t } = useLocales()
  const navigate = useNavigate()

  const { id } = useParams()
  const queryClient = useQueryClient()

  const [form] = Form.useForm()

  const onMutationSuccess = () => {
    if (id) {
      queryClient.invalidateQueries({
        queryKey: [useCounterpartServiceGetCounterpartEndpointKey, String(id)]
      })
    } else {
      queryClient.invalidateQueries({
        queryKey: [useCounterpartServiceGetCounterpartListEndpointKey]
      })
    }
    onBackClick()
  }

  const { mutate } = useCounterpartServiceCreateCounterpartEndpoint({
    onSuccess: () => {
      onMutationSuccess()
      message.success(t('counterparts.provider_created'))
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })

  const { mutate: edit } = useCounterpartServiceUpdateCounterpartEndpoint({
    onSuccess: () => {
      onMutationSuccess()
      message.success(t('common.save_success'))
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })

  const { data, isLoading } = useCounterpartServiceGetCounterpartEndpoint(
    {
      id: String(id)
    },
    [],
    {
      enabled: !!id
    }
  )

  const onBackClick = () => {
    form.resetFields()
    navigate(id ? paths.counterpart.details(id) : paths.counterpart.root)
  }

  const onSubmit = async () => {
    const formFields = await form.validateFields()

    const values = {
      ...formFields,
      status: formFields.status
        ? LegalEntityStatus.ACTIVE
        : LegalEntityStatus.INACTIVE
    }

    id
      ? edit({
          id: String(id),
          requestBody: values
        })
      : mutate({
          requestBody: values
        })
  }

  useEffect(() => {
    if (id && data) {
      form.setFieldsValue({
        ...data,
        status: data.status === LegalEntityStatus.ACTIVE
      })
    }
  }, [data, id])

  if (isLoading) {
    return <LoadingScreen />
  }

  return (
    <PageBreadcrumbs
      title={id ? String(data?.name) : t('counterparts.add_counterpart')}
      items={[
        {
          title: t('counterparts.title'),
          path: paths.counterpart.root
        },
        {
          title: id ? String(data?.name) : t('counterparts.add_counterpart')
        }
      ]}
    >
      <Flex vertical gap={24}>
        <Button
          icon={<LeftOutlined />}
          className="fit-content"
          onClick={onBackClick}
        >
          {t('common.back')}
        </Button>
        <CounterPartDetails form={form} hasId={!!id} />
      </Flex>

      <FormFooter
        onCancel={onBackClick}
        onSave={onSubmit}
        saveConfirmTitle={`${t(id ? 'common.save' : 'common.add')}?`}
        saveLabel={t(id ? 'common.save' : 'common.add')}
      />
    </PageBreadcrumbs>
  )
}
